import {
  addMonths,
  addWeeks,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  isSameDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import React from 'react';

import {
  Button,
  Card,
  CardContent,
  TimeSlot,
} from '../../../components/shared';
import { useDateLocale, useLanguage } from '../../../hooks/shared';
import { cn } from '../../../lib/utils';
import { CalendarView, DaySchedule, Training } from '../types/calendarTypes';

interface CalendarProps {
  schedules: DaySchedule[];
  onDateSelect?: (date: Date) => void;
  onTimeSlotClick?: (date: string, timeSlotId: string) => void;
  selectedDate?: Date | null;
  selectedTimeSlotId?: string;
  onCalendarViewChange?: (view: CalendarView) => void;
  currentView?: CalendarView;
  currentDate: Date; // Make this required
  onCurrentDateChange: (date: Date) => void; // Make this required
  onEdit?: (trainingId: string) => void;
  onDelete?: (trainingId: string) => void;
  onCancelEvent?: (trainingId: string) => void;
}

const Calendar: React.FC<CalendarProps> = ({
  schedules,
  onDateSelect,
  onTimeSlotClick,
  selectedDate,
  selectedTimeSlotId,
  onCalendarViewChange,
  currentView,
  currentDate,
  onCurrentDateChange,
}) => {
  const { t } = useLanguage();
  const { format } = useDateLocale();

  const scrollToToday = () => {
    // Try multiple times in case navigation/rendering delays the element
    let attempts = 0;
    const tryScroll = () => {
      const el = document.querySelector(
        '[data-calendar-today="true"]'
      ) as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (attempts < 5) {
        attempts += 1;
        setTimeout(tryScroll, 80);
      }
    };
    // Kick off after current tick
    setTimeout(tryScroll, 0);
  };

  const weekDays = eachDayOfInterval({
    start: startOfWeek(currentDate, { weekStartsOn: 1 }), // Monday
    end: endOfWeek(currentDate, { weekStartsOn: 1 }),
  });

  const monthWeeks = eachWeekOfInterval(
    {
      start: startOfMonth(currentDate),
      end: endOfMonth(currentDate),
    },
    { weekStartsOn: 1 }
  ).map((weekStart) =>
    eachDayOfInterval({
      start: weekStart,
      end: endOfWeek(weekStart, { weekStartsOn: 1 }),
    })
  );

  const navigateWeek = (direction: 'prev' | 'next') => {
    onCurrentDateChange(
      direction === 'next' ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1)
    );
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    onCurrentDateChange(
      direction === 'next'
        ? addMonths(currentDate, 1)
        : subMonths(currentDate, 1)
    );
  };

  // Notify parent component about view change
  const handleViewChange = (newView: CalendarView) => {
    onCalendarViewChange?.(newView);
  };

  return (
    <div className="space-y-4">
      {/* Navigation */}
      <div className="space-y-3">
        {/* Navigation arrows */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (currentView === 'week') {
                navigateWeek('prev');
              } else if (currentView === 'month') {
                navigateMonth('prev');
              }
            }}
            className="flex-shrink-0"
          >
            ←
          </Button>

          <div className="flex-1 text-center">
            <h2 className="text-lg font-semibold text-gray-800">
              {currentView === 'week'
                ? `${format(weekDays[0], 'd MMMM')} - ${format(weekDays[6], 'd MMMM yyyy')}`
                : format(currentDate, 'MMMM yyyy')}
            </h2>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (currentView === 'week') {
                navigateWeek('next');
              } else if (currentView === 'month') {
                navigateMonth('next');
              }
            }}
            className="flex-shrink-0"
          >
            →
          </Button>
        </div>

        {/* Control buttons */}
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onCurrentDateChange(new Date());
              scrollToToday();
            }}
            className="px-3"
          >
            {t('calendar.today')}
          </Button>
          <Button
            variant={currentView === 'week' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleViewChange('week')}
          >
            {t('calendar.week')}
          </Button>
          <Button
            variant={currentView === 'month' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleViewChange('month')}
          >
            {t('calendar.month')}
          </Button>
        </div>
      </div>

      {/* Calendar */}
      {currentView === 'week' ? (
        <WeekView
          weekDays={weekDays}
          schedules={schedules}
          onDateSelect={onDateSelect}
          onTimeSlotClick={onTimeSlotClick}
          selectedDate={selectedDate}
          selectedTimeSlotId={selectedTimeSlotId}
        />
      ) : (
        <MonthView
          monthWeeks={monthWeeks}
          schedules={schedules}
          onDateSelect={onDateSelect}
        />
      )}
    </div>
  );
};

// Week view component (vertical layout)
interface WeekViewProps {
  weekDays: Date[];
  schedules: DaySchedule[];
  onDateSelect?: (date: Date) => void;
  onTimeSlotClick?: (date: string, timeSlotId: string) => void;
  selectedDate?: Date | null;
  selectedTimeSlotId?: string;
}

const WeekView: React.FC<WeekViewProps> = ({
  weekDays,
  schedules,
  onDateSelect,
  onTimeSlotClick,
  selectedDate,
  selectedTimeSlotId,
}) => {
  const { t } = useLanguage();
  const { format } = useDateLocale();

  const getScheduleForDate = (date: Date): DaySchedule | undefined => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return schedules.find((schedule) => schedule.date === dateStr);
  };

  return (
    <div className="space-y-3">
      {weekDays.map((day) => {
        const schedule = getScheduleForDate(day);
        const isToday = isSameDay(day, new Date());
        const isSelected = selectedDate && isSameDay(day, selectedDate);

        return (
          <Card
            key={day.toISOString()}
            className={cn(
              'hover:shadow-md transition-shadow',
              isToday && 'ring-2 ring-blue-500',
              isSelected && 'ring-4 ring-purple-500 shadow-lg'
            )}
            data-calendar-today={isToday ? 'true' : undefined}
          >
            <CardContent className="p-4">
              {/* Day header - clickable to go to day view */}
              <div
                className="flex items-center justify-between mb-3 cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
                onClick={() => onDateSelect?.(day)}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'text-lg font-bold rounded-full w-10 h-10 flex items-center justify-center',
                      isToday
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-800'
                    )}
                  >
                    {format(day, 'd')}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">
                      {format(day, 'EEEE')}
                    </div>
                    <div className="text-sm text-gray-500">
                      {format(day, 'd MMMM yyyy')}
                    </div>
                  </div>
                </div>

                {/* Training count indicator */}
                {schedule?.timeSlots.length ? (
                  <div className="text-sm text-gray-600">
                    {t('calendar.trainingsCount', {
                      count: schedule.timeSlots.length,
                    })}
                  </div>
                ) : null}
              </div>

              {/* Time slots - clickable to go directly to slot details */}
              {schedule?.timeSlots.length ? (
                <div className="space-y-3">
                  {schedule.timeSlots
                    .sort((a, b) => a.startTime.localeCompare(b.startTime))
                    .map((training) => (
                      <TimeSlot
                        key={training.id}
                        training={training}
                        compact={true}
                        showLevelText={false}
                        onClick={() => {
                          onTimeSlotClick?.(schedule.date, training.id);
                        }}
                        className={cn(
                          'cursor-pointer',
                          selectedTimeSlotId === training.id
                            ? 'ring-4 ring-blue-500 ring-opacity-50 shadow-lg'
                            : ''
                        )}
                      />
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <div className="text-4xl mb-2">🏃‍♀️</div>
                  <p>{t('calendar.noTrainings')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Month view component
interface MonthViewProps {
  monthWeeks: Date[][];
  schedules: DaySchedule[];
  onDateSelect?: (date: Date) => void;
}

const MonthView: React.FC<MonthViewProps> = ({
  monthWeeks,
  schedules,
  onDateSelect,
}) => {
  const { format } = useDateLocale();

  const getScheduleForDate = (date: Date): DaySchedule | undefined => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return schedules.find((schedule) => schedule.date === dateStr);
  };

  // Function to calculate training position in day strip
  const getTimePosition = (training: Training) => {
    const dayStart = 6; // 6:00 - day start
    const dayEnd = 22; // 22:00 - day end
    const dayDuration = dayEnd - dayStart;

    // Parse start time (assume training lasts 1 hour)
    const [hours, minutes] = training.startTime.split(':').map(Number);
    const startTime = hours + minutes / 60;
    const endTime = startTime + 1; // Training lasts 1 hour

    // Calculate position and height
    const position = Math.max(0, (startTime - dayStart) / dayDuration) * 100;
    const height = Math.max(2, ((endTime - startTime) / dayDuration) * 100);

    return { position, height };
  };

  return (
    <div className="space-y-1">
      {/* Week day headers */}
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: 7 }, (_, i) => {
          const day = new Date(2024, 0, i + 1); // Use any Monday as reference
          return (
            <div
              key={day.toISOString()}
              className="text-center p-2 text-sm font-medium text-gray-600"
            >
              {format(day, 'EEE')}
            </div>
          );
        })}
      </div>

      {/* Month weeks */}
      {monthWeeks.map((week, weekIndex) => (
        <div key={weekIndex} className="grid grid-cols-7 gap-1">
          {week.map((day) => {
            const schedule = getScheduleForDate(day);
            const isToday = isSameDay(day, new Date());
            const isCurrentMonth = day.getMonth() === new Date().getMonth();

            return (
              <div
                key={day.toISOString()}
                className={cn(
                  'min-h-[80px] p-1 border rounded cursor-pointer hover:bg-gray-50 transition-colors',
                  !isCurrentMonth && 'bg-gray-100 text-gray-400',
                  isToday && 'ring-2 ring-blue-500'
                )}
                data-calendar-today={isToday ? 'true' : undefined}
                onClick={() => onDateSelect?.(day)}
              >
                <div
                  className={cn(
                    'text-sm font-medium mb-1',
                    isToday && 'text-blue-600'
                  )}
                >
                  {format(day, 'd')}
                </div>

                {schedule?.timeSlots.length ? (
                  <div className="mt-2 relative h-12">
                    {/* Single vertical strip with time-based positioning */}
                    {schedule.timeSlots.map((training) => {
                      const { position, height } = getTimePosition(training);

                      return (
                        <div
                          key={training.id}
                          className="absolute bg-blue-400 rounded-sm w-full"
                          style={{
                            top: `${position}%`,
                            height: `${height}%`,
                            minHeight: '2px',
                          }}
                          title={`${training.startTime} - ${training.level}`}
                        />
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default Calendar;
